import React, { useEffect, useState } from 'react'
import * as S from '@/components/Forms/styles'

import Password from '@/components/Inputs/Password'

import { changeInputClass, validations } from '@/services/functions'
import {
  ENDPOINTS,
  sendPostRequest,
  handleResponseStates
} from '@/services/api'
import {
  passwordTooShort,
  passwordsDontMatch,
  onExpiredCode,
  onPasswordReset,
  onUncaughtError,
  onTooManyFailedAttempts,
  onLimitExceeded
} from '@/services/userAlerts'

import { useTranslation } from 'react-i18next'

const FormResetPassword = props => {
  const {
    userNameState,
    passwordHandler,
    loginHandler,
    requestTokenHandler,
    loadHandler,
    screenHandler,
    screenIds
  } = props

  const { t } = useTranslation()

  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [dataValid, setDataValid] = useState(false)

  const [labelsEmail, setLabelsEmail] = useState('')
  const [labelsToken, setLabelsToken] = useState('')
  const [labelsNewPassword, setLabelsNewPassword] = useState('')
  const [labelsRepeatPassword, setLabelsRepeatPassword] = useState('')
  const [placeholdersEmail, setPlaceholdersEmail] = useState('')
  const [placeholdersToken, setPlaceholdersToken] = useState('')
  const [placeholdersNewPassword, setPlaceholdersNewPassword] = useState('')
  const [subtitlesFillInfo, setSubtitlesFillInfo] = useState('')
  const [buttonsResetPassword, setButtonsResetPassword] = useState('')
  const [buttonsResendToken, setButtonsResendToken] = useState('')

  let post = false
  let newPasswordValidation = false
  let repeatPasswordValidation = false
  let resetTokenValidation = false

  useEffect(() => {
    setLabelsEmail(t('labels.email'))
    setLabelsToken(t('labels.token'))
    setLabelsNewPassword(t('labels.newPassword'))
    setLabelsRepeatPassword(t('labels.repeatPassword'))
    setPlaceholdersEmail(t('placeholders.email'))
    setPlaceholdersToken(t('placeholders.token'))
    setPlaceholdersNewPassword(t('placeholders.newPassword'))
    setSubtitlesFillInfo(t('subtitles.fillInfo'))
    setButtonsResetPassword(t('buttons.resetPassword'))
    setButtonsResendToken(t('buttons.resendToken'))
  }, [t])

  useEffect(() => {
    changeInputClass(
      'inp_newpass_reset_pass',
      newPassword,
      post,
      newPasswordValidation
    )
  }, [newPassword, post, newPasswordValidation])

  useEffect(() => {
    changeInputClass(
      'inp_repeatpass_reset_pass',
      repeatPassword,
      post,
      repeatPasswordValidation
    )
  }, [repeatPassword, post, repeatPasswordValidation])

  useEffect(() => {
    changeInputClass(
      'inp_token_reset_pass',
      resetToken,
      post,
      resetTokenValidation
    )
  }, [resetToken, post, resetTokenValidation])

  useEffect(() => {
    const passValidation = newPassword.length >= 8
    const repeatPassValidation = repeatPassword.length >= 8

    setDataValid(passValidation && repeatPassValidation && resetToken)
  }, [newPassword, repeatPassword, resetToken])

  useEffect(() => {
    const btn = document.getElementById('btn_resetpass')
    if (!btn) return

    if (dataValid) {
      btn.classList.remove('disabled')
      btn.classList.add('blue')
    } else {
      btn.classList.remove('blue')
      btn.classList.add('disabled')
    }
  }, [dataValid])

  const handleSubmit = e => {
    post = true
    e.preventDefault()
    resetPassword()
  }

  const onResetSuccess = () => {
    screenHandler(screenIds.login)
    passwordHandler(newPassword)
    onPasswordReset(loginHandler, userNameState, newPassword)
  }

  const validateResetPassData = () => {
    newPasswordValidation = validations.password(
      newPassword,
      'inp_newpass_reset_pass',
      post
    )
    repeatPasswordValidation = validations.repeatPassword(
      newPassword,
      repeatPassword,
      'inp_repeatpass_reset_pass',
      post
    )
    resetTokenValidation = validations.required(
      resetToken,
      'inp_token_reset_pass',
      post
    )

    if (!resetTokenValidation) {
      return false
    } else if (!newPasswordValidation) {
      passwordTooShort()
      return false
    } else if (!repeatPasswordValidation) {
      passwordsDontMatch()
      return false
    }

    return true
  }

  const resetPassword = async () => {
    if (!validateResetPassData()) return

    loadHandler(true)

    await sendPostRequest(ENDPOINTS.confirm_password, {
      username: userNameState,
      password: newPassword,
      code: resetToken
    })
      .then(response => {
        loadHandler(false)
        handleResponseStates(response, responsesResetPass)
      })
      .catch(error => {
        const msg = error.data || error
        console.error('resetPassword error: ', msg)
        onUncaughtError()
      })
  }

  const responsesResetPass = [
    { name: 200, callback: onResetSuccess },
    { name: 'ExpiredCodeException', callback: onExpiredCode },
    { name: 'CodeMismatchException', callback: onExpiredCode },
    { name: 'LimitExceededException', callback: onLimitExceeded },
    {
      name: 'TooManyFailedAttemptsException',
      callback: onTooManyFailedAttempts
    }
  ]

  return (
    <div id="reset-password">
      <div className="title">
        <h1>{buttonsResetPassword}</h1>
        <h2>{subtitlesFillInfo}</h2>
      </div>

      <S.FormWrapper onSubmit={handleSubmit}>
        <section>
          <label>{labelsEmail}</label>
          <input
            id="inp_email_reset_pass"
            type="email"
            placeholder={placeholdersEmail}
            value={userNameState}
            disabled
          />
        </section>

        <section>
          <label>{labelsNewPassword}</label>
          <Password
            id="inp_newpass_reset_pass"
            placeholder={placeholdersNewPassword}
            inputState={newPassword}
            inputHandler={setNewPassword}
          />
        </section>

        <section>
          <label>{labelsRepeatPassword}</label>
          <Password
            id="inp_repeatpass_reset_pass"
            placeholder={labelsRepeatPassword}
            inputState={repeatPassword}
            inputHandler={setRepeatPassword}
          />
        </section>

        <section>
          <label>{labelsToken}</label>
          <input
            id="inp_token_reset_pass"
            type="text"
            placeholder={placeholdersToken}
            value={resetToken}
            onChange={e => setResetToken(e.target.value)}
          />
          <div id="btn_wrapper">
            <button
              id="btn_resendtoken"
              type="button"
              className="link-button"
              onClick={requestTokenHandler}
            >
              {buttonsResendToken}
            </button>
          </div>
        </section>

        <button
          id="btn_resetpass"
          type="submit"
          className="btn lg disabled"
          aria-label="btn_confirm"
        >
          {buttonsResetPassword}
        </button>
      </S.FormWrapper>
    </div>
  )
}

export default FormResetPassword
